export const COUNTDOWN_ATTRIBUTES = {
  IS_COUNTDOWN: 'data-is-pmw-countdown',
  BLOCK_ID: 'data-pmw-countdown-id',
  TIMEZONE: 'data-pmw-countdown-timezone',
  TARGET_UTC: 'data-pmw-countdown-target',
  SHOW_DAYS: 'data-pmw-countdown-show-days',
  SHOW_LABELS: 'data-pmw-countdown-show-labels',
  LABEL_DAYS: 'data-pmw-countdown-label-days',
  LABEL_HOURS: 'data-pmw-countdown-label-hours',
  LABEL_MINUTES: 'data-pmw-countdown-label-minutes',
  LABEL_SECONDS: 'data-pmw-countdown-label-seconds',
  UNIT: 'data-pmw-countdown-unit',
} as const;

export const COUNTDOWN_STYLE_PROPS = {
  SEPARATOR: 'pmw-countdown-separator',
  LABEL_SIZE: 'pmw-countdown-label-size',
  LABEL_COLOR: 'pmw-countdown-label-color',
  LABEL_FONT: 'pmw-countdown-label-font',
  PLACEMENT: 'pmw-countdown-placement',
} as const;

export const COUNTDOWN_SEPARATOR = {
  NONE: 'none',
  COLON: 'colon',
  DOT: 'dot',
} as const;

export type CountdownSeparator = (typeof COUNTDOWN_SEPARATOR)[keyof typeof COUNTDOWN_SEPARATOR];

export const COUNTDOWN_SEPARATOR_GLYPHS: Record<CountdownSeparator, string> = {
  [COUNTDOWN_SEPARATOR.NONE]: '',
  [COUNTDOWN_SEPARATOR.COLON]: ':',
  [COUNTDOWN_SEPARATOR.DOT]: '·',
};

export const COUNTDOWN_PLACEMENT = {
  SPREAD: 'spread',
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} as const;

export type CountdownPlacement = (typeof COUNTDOWN_PLACEMENT)[keyof typeof COUNTDOWN_PLACEMENT];

export const COUNTDOWN_FILLER_FULL_WIDTH = '100%';
export const COUNTDOWN_FILLER_HALF_WIDTH = '50%';
export const COUNTDOWN_WIDTH_FIT_CONTENT = '1%';

export const COUNTDOWN_TICK_INTERVAL_MS = 1000;

export const COUNTDOWN_ATTRIBUTE_ON = '1';
export const COUNTDOWN_ATTRIBUTE_OFF = '0';

export const COUNTDOWN_DEFAULT_DEADLINE_DAYS_AHEAD = 7;
export const COUNTDOWN_DEFAULT_FONT_FAMILY = 'Helvetica, sans-serif';
export const COUNTDOWN_DEFAULT_NUMBER_SIZE = '20px';
export const COUNTDOWN_DEFAULT_NUMBER_COLOR = '#242424';
export const COUNTDOWN_DEFAULT_LABEL_SIZE = '14px';
export const COUNTDOWN_DEFAULT_LABEL_COLOR = '#757589';
export const COUNTDOWN_NUMBER_LINE_HEIGHT = '120%';
export const COUNTDOWN_DIGIT_STYLE: Record<string, string> = {'line-height': COUNTDOWN_NUMBER_LINE_HEIGHT, 'min-width': '1.4em'};
export const COUNTDOWN_LABEL_LINE_HEIGHT = '130%';
export const COUNTDOWN_LABEL_GAP = '4px';

export const COUNTDOWN_UNIT_CELL_PADDING = '12px 16px';
export const COUNTDOWN_SEPARATOR_CELL_PADDING = '12px 0';

export const COUNTDOWN_HOURS_PER_DAY = 24;
export const COUNTDOWN_MINUTES_PER_HOUR = 60;
export const COUNTDOWN_SECONDS_PER_MINUTE = 60;
export const COUNTDOWN_MILLISECONDS_PER_SECOND = 1000;

export enum CountdownUnit {
  DAYS = 'days',
  HOURS = 'hours',
  MINUTES = 'minutes',
  SECONDS = 'seconds',
}

export interface CountdownDigits {
  showDays: boolean;
  unitValues: Record<CountdownUnit, string>;
}

export const COUNTDOWN_UNIT_LABEL_ATTRIBUTES: Record<CountdownUnit, string> = {
  [CountdownUnit.DAYS]: COUNTDOWN_ATTRIBUTES.LABEL_DAYS,
  [CountdownUnit.HOURS]: COUNTDOWN_ATTRIBUTES.LABEL_HOURS,
  [CountdownUnit.MINUTES]: COUNTDOWN_ATTRIBUTES.LABEL_MINUTES,
  [CountdownUnit.SECONDS]: COUNTDOWN_ATTRIBUTES.LABEL_SECONDS,
};

export const COUNTDOWN_UNIT_DEFAULT_LABEL_KEYS: Record<CountdownUnit, string> = {
  [CountdownUnit.DAYS]: 'pmwjs_countdown_days',
  [CountdownUnit.HOURS]: 'pmwjs_countdown_hours',
  [CountdownUnit.MINUTES]: 'pmwjs_countdown_minutes',
  [CountdownUnit.SECONDS]: 'pmwjs_countdown_seconds',
};

export const COUNTDOWN_UNIT_ORDER: readonly CountdownUnit[] = [CountdownUnit.DAYS, CountdownUnit.HOURS, CountdownUnit.MINUTES, CountdownUnit.SECONDS];

export interface CountdownWallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

export interface CountdownDeadline {
  timezoneCode: string;
  targetUtcSeconds: number;
}

export interface CountdownConfig {
  showDays: boolean;
  showLabels: boolean;
  labels: Record<CountdownUnit, string>;
  separator: CountdownSeparator;
  placement: CountdownPlacement;
  hasFixedWidth: boolean;
  unitValues: Record<CountdownUnit, string>;
  labelSize: string;
  labelColor: string;
  labelFont: string;
}
